import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Settings2, Brain, Globe, X, MessagesSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SEARCH_SUPPORTED_MODELS,
  REASONING_UNSUPPORTED_MODELS,
} from "@/store/zustand/aiActionStore";
import AnimatedToggle from "@/components/ui/animated-toggle";
import { useAiContextStore } from "@/store/zustand/aiContextStore";

const FeatureToggleMenu = ({
  isChatLoading,
  useReasoning,
  setUseReasoning,
  useSearch,
  useChatOnly,
  setUseChatOnly,
  codeGenerationModel,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Check if reasoning is supported for current model
  const isReasoningSupported =
    !REASONING_UNSUPPORTED_MODELS.includes(codeGenerationModel);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 p-1 rounded-md transition-colors bg-secondary-n-950"
        >
          <Settings2
            className={cn(
              "h-3.5 w-3.5 transition-colors",
              isOpen ? "text-secondary-n-50" : "text-icons-secondary",
            )}
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="rounded-lg px-0 py-2 w-64"
        side="top"
      >
        <div className="flex flex-col gap-2 px-2">
          <div className="flex items-center justify-between gap-4  cursor-pointer">
            <div className="flex items-center gap-2">
              <Brain
                className={cn(
                  "h-4 w-4",
                  !isReasoningSupported
                    ? "text-secondary-n-700"
                    : "text-secondary-n-600",
                )}
              />
              <span
                className={cn(
                  "text-xs",
                  !isReasoningSupported && "text-secondary-n-700",
                )}
              >
                Reasoning
              </span>
            </div>

            <AnimatedToggle
              icon={
                <X
                  className={cn(
                    "absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium",
                  )}
                />
              }
              disabled={isChatLoading || !isReasoningSupported}
              isOn={useReasoning && isReasoningSupported}
              onChange={() => {
                if (!isChatLoading && isReasoningSupported) {
                  setUseReasoning(!useReasoning);
                }
              }}
              className="p-2"
            />
          </div>
          {SEARCH_SUPPORTED_MODELS.has(codeGenerationModel) && (
            <div className="flex items-center justify-between gap-4  cursor-pointer">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-secondary-n-600" />
                <span className="text-xs">Web Search</span>
              </div>

              <AnimatedToggle
                icon={
                  <X
                    className={cn(
                      "absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium",
                    )}
                  />
                }
                disabled={isChatLoading}
                isOn={useSearch}
                onChange={() => {
                  if (isChatLoading) {
                    return;
                  }

                  useAiContextStore.getState().setUseSearch(!useSearch);
                }}
                className="p-2"
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-4  cursor-pointer">
            <div className="flex items-center gap-2">
              <MessagesSquare className="h-4 w-4 text-secondary-n-600" />
              <span className="text-xs">Chat Only</span>
            </div>

            <AnimatedToggle
              icon={
                <X
                  className={cn(
                    "absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium",
                  )}
                />
              }
              disabled={isChatLoading}
              isOn={useChatOnly}
              onChange={() => {
                if (!isChatLoading) {
                  setUseChatOnly(!useChatOnly);
                }
              }}
              className="p-2"
            />
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FeatureToggleMenu;
